import { useState, useEffect, useRef } from "react";
import {
  nextFrame,
  useDelayedState,
  useMaybePubSub,
  useDebug,
} from "@huub-web/ui";
import { isArray, isObject, isString } from "@huub-web/utils";

import { useAPI } from "./useAPI";

/**
 * Hook for fetching templates from the backend.
 *
 * The shape of the returned collection is opaque to this hook; consumers
 * pass their own template type via the `TTemplates` generic parameter
 * (e.g. `useTemplatesAPI<TemplateCollection>(endpoint)`).
 *
 * @param endpoint The endpoint to fetch the templates from.
 */
export const useTemplatesAPI = <TTemplates extends unknown[] = unknown[]>(
  endpoint?: string,
) => {
  const { $get } = useAPI();
  const pubSub = useMaybePubSub();
  const { log } = useDebug();

  const [data, setData] = useState<TTemplates>([] as unknown as TTemplates);
  const [error, setError] = useState<string | null>(null);
  const [refreshCount, setRefreshCount] = useState(0);

  /**
   * Whether a fetch is currently in progress. Unlike `loading`, this is
   * always `true` during any fetch (initial or refresh) and flips `false`
   * synchronously when the response arrives. Used by `usePageReady` to
   * delay the page fade-in until content actually exists.
   */
  const [isFetching, setIsFetching] = useState(false);

  /**
   * Delayed loading flag for UI indicators (progress bars, skeletons).
   * Only set to `true` immediately for refreshes to avoid a flash of
   * loading UI on initial page navigations.
   */
  const [loading, setLoading, setImmidiateLoading] = useDelayedState<boolean>(
    false,
    500,
  );

  const isInitializedEndpointRef = useRef<string>("");
  const isInitializedRefreshCountRef = useRef(0);

  useEffect(() => {
    if (!endpoint) {
      return;
    }

    // Check if we need to fetch (before updating refs)
    const shouldFetch =
      isInitializedEndpointRef.current !== endpoint ||
      isInitializedRefreshCountRef.current !== refreshCount;

    if (!shouldFetch) {
      return;
    }

    log("orange", "useTemplatesAPI", "Fetching for endpoint", endpoint);

    // Determine if this is a refresh (same endpoint, different count)
    const isRefresh = isInitializedEndpointRef.current === endpoint;

    setIsFetching(true);
    setImmidiateLoading(isRefresh);
    setError(null);

    // Update refs BEFORE async operation
    isInitializedEndpointRef.current = endpoint;
    isInitializedRefreshCountRef.current = refreshCount;

    const fetchData = async () => {
      try {
        const response = await $get(endpoint);

        if (response?.data) {
          log("orange", "useTemplatesAPI", "Fetched data", response.data);

          if (isArray(response.data)) {
            setData(response.data as TTemplates);
          } else if (
            isObject(response.data) &&
            "templates" in response.data &&
            isArray(response.data.templates)
          ) {
            setData(response.data.templates as TTemplates);

            if ("title" in response.data && isString(response.data.title)) {
              nextFrame(() => {
                pubSub?.publish("titleUpdate", {
                  title: response.data.title,
                });
              });
            }
          } else {
            setError("Invalid response");
          }
        } else {
          console.error("[useTemplatesAPI] Invalid response", response);
          setError("Invalid response");
        }
      } catch (err) {
        if (err instanceof Error) {
          setError(err.message);
        } else {
          setError(String(err));
        }
        console.error("[useTemplatesAPI] Error", err);
      } finally {
        setIsFetching(false);
        setLoading(false);
      }
    };

    fetchData();
  }, [endpoint, refreshCount]);

  useEffect(() => {
    if (!pubSub) {
      return;
    }

    const unsubscribe = pubSub.subscribe("refreshTemplates", ({ id }) => {
      if (id) {
        return;
      }
      log(
        "cyan",
        "useTemplatesAPI",
        "refreshTemplates, refreshing main content",
      );
      setRefreshCount((prev) => prev + 1);
    });

    return () => unsubscribe();
  }, [pubSub]);

  return { data, loading, isFetching, error };
};
